import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import { Modal, Input, Form } from 'antd';
import { useSelector } from 'react-redux';
import alert from '@components/alert';
import moment from 'moment';
import {
  getVerificationCode,
  checkVerificationCode,
} from '@service/apis/commonService.api';
import './index.scss';
const { Button } = require('appkit-react');
const FormItem = Form.Item;

export default forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [isModalShow, toggleModalShow] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifing, setIsVerifing] = useState(false);
  const [countDown, setCountDown] = useState(0);
  const sendCount = useRef(0);
  const configRef = useRef(null);

  const userInfo = useSelector(({ login }) => login.userInfo);

  useImperativeHandle(ref, () => ({
    show(cfg) {
      configRef.current = cfg;
      toggleModalShow(true);
    },
    hide() {
      toggleModalShow(false);
    },
  }));

  useEffect(() => {
    if (userInfo && userInfo.email) {
      form.setFieldsValue({ email: userInfo.email });
      handleSendCode();
    }
  }, [userInfo, isModalShow]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountDown((count) => (count > 0 ? count - 1 : count));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSendCode = async () => {
    if (isSendingCode || countDown || !isModalShow) {
      return;
    }
    try {
      // Only validate email field, not the code field (which is empty at this point)
      await form.validateFields(['email']);
      setIsSendingCode(true);
      sendCount.current += 1;
      const res = await getVerificationCode();
      setIsSendingCode(false);
      if (res && res.result) {
        setCountDown(60);
      }
      if (res && res.sendDatetime) {
        const diff = moment(res.sendDatetime).diff(moment(), 'seconds');
        if (diff && diff <= 60) {
          setCountDown(60 - Math.abs(diff));
          res.message &&
            alert.warning({
              content: res.message,
            });
        }
      }
    } catch (err) {
      setIsSendingCode(false);
      console.log('Email validation failed:', err);
    }
  };

  const handleVerify = async () => {
    const formValues = await form.validateFields();
    setIsVerifing(true);
    const res = await checkVerificationCode(formValues.code);
    setIsVerifing(false);
    if (res && res.result) {
      configRef.current &&
        configRef.current.callback &&
        configRef.current.callback();
      handleCancel();
    } else {
      res.message &&
        form.setFields([
          {
            name: 'code',
            errors: [res.message],
          },
        ]);
    }
  };

  const handleCancel = () => {
    toggleModalShow(false);
    setIsSendingCode(false);
    setIsVerifing(false);
    setCountDown(0);
    form.resetFields();
    sendCount.current = 0;
    configRef.current = null;
  };

  return (
    <Modal
      visible={isModalShow}
      title={null}
      footer={null}
      onCancel={handleCancel}
      className="email-verification-modal"
      maskClosable={false}
    >
      <div className="email-verification-wrap">
        <div className="email-verification-title">Email Verification</div>
        <div className="email-verification-subtitle">
          We will send a verification code to your Email, please enter below.
        </div>

        <Form form={form} layout="vertical">
          <FormItem
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Required' },
              { type: 'email', message: 'Email is not a valid email' },
            ]}
          >
            <Input readOnly size="large" style={{ height: 48 }} />
          </FormItem>

          <FormItem label="Verification Code" shouldUpdate>
            <div className="verification-row">
              <FormItem
                noStyle
                name="code"
                rules={[
                  { required: true, message: 'Verification code is required' },
                ]}
              >
                <Input size="large" autoComplete="off" style={{ height: 48 }} />
              </FormItem>

              <Button
                size="lg"
                style={{ width: 170, height: 48, marginLeft: 10 }}
                isLoading={isSendingCode}
                onClick={handleSendCode}
              >
                {countDown
                  ? `Resend(${countDown}s)`
                  : sendCount.current > 0
                  ? 'Resend'
                  : 'Send Code'}
              </Button>
            </div>
          </FormItem>
        </Form>

        <div
          className="verification-row"
          style={{ gap: 20, width: '85%', marginTop: 30 }}
        >
          <Button
            size="lg"
            kind="secondary"
            style={{ flex: 1, height: 48 }}
            onClick={handleCancel}
          >
            Cancel
          </Button>

          <Button
            size="lg"
            style={{ flex: 1, height: 48 }}
            onClick={handleVerify}
            isLoading={isVerifing}
          >
            Verify Account
          </Button>
        </div>
      </div>
    </Modal>
  );
});
